import { computeJointMarkerSizesFromModelGroup } from "../../scene/jointMarkerSizing";
import { deactivatePosPick } from "../../../runtime/posPickState";
import * as RuntimeRegistry from "../../../runtime/objectRegistry";
import { logger } from "../../../utils/logger";

export function hidePosPickMarker(scene) {
  const marker = scene?.getObjectByName("__posPickMarker");
  if (marker) {
    marker.visible = false;
  }
  deactivatePosPick();
}

export function publishSceneObjects(sceneObjectsRef, setSceneObjects) {
  setSceneObjects({ ...sceneObjectsRef.current });
  try {
    RuntimeRegistry.setAll(sceneObjectsRef.current);
  } catch (error) {
    logger.warn("[Scene] Unexpected error during scene update", error);
  }
}

export function clearRuntimeRegistry() {
  try {
    RuntimeRegistry.clear();
  } catch (error) {
    logger.warn("[Scene] Unexpected error during scene update", error);
  }
}

export function updateSceneRebuildRefs({
  bodyTree,
  bodyTreeStructureSignatureRef,
  currentFileKeys,
  fileMapSignatureRef,
  initialBuildDoneRef,
  newFileMapSig,
  newStructureSig,
  previousBodiesRef,
  previousFileMapKeysRef,
}) {
  bodyTreeStructureSignatureRef.current = newStructureSig;
  previousBodiesRef.current = JSON.parse(JSON.stringify(bodyTree));
  fileMapSignatureRef.current = newFileMapSig;
  previousFileMapKeysRef.current = currentFileKeys;
  if (initialBuildDoneRef) {
    initialBuildDoneRef.current = true;
  }
}

export function shouldUseStructureSignatureFastPath({
  allowAssetChange,
  existingModel,
  nextStructureSignature,
  previousStructureSignature,
  sawStructureChangeSignal,
}) {
  return Boolean(
    existingModel &&
    previousStructureSignature &&
    previousStructureSignature === nextStructureSignature &&
    allowAssetChange &&
    !sawStructureChangeSignal
  );
}

export function scheduleJointMarkerSizing({
  applyJointMarkerSizes,
  jointMarkerScalePercent,
  newFileMapSig,
  newStructureSig,
  sceneRef,
}) {
  setTimeout(() => {
    if (!sceneRef.current) return;
    const modelGroup = sceneRef.current.getObjectByName("modelGroup");
    if (!modelGroup) return;
    const sizes = computeJointMarkerSizesFromModelGroup(modelGroup, jointMarkerScalePercent);
    if (!sizes) return;
    try {
      const autoResizeKey = `${newStructureSig}|${newFileMapSig}|${jointMarkerScalePercent}`;
      applyJointMarkerSizes(sizes, {
        requestRebuildOnChange: true,
        autoResizeKey,
      });
    } catch (error) {
      logger.warn("[Scene] Unexpected error during scene update", error);
    }
  }, 0);
}

export function registerPendingMeshLoadFactory(registerPendingBodyMeshLoad, pendingMeshLoads) {
  return (promise, bodyUuid) => {
    if (promise && typeof promise.then === "function") {
      pendingMeshLoads.push(Promise.resolve(promise));
      registerPendingBodyMeshLoad(promise, bodyUuid);
    }
  };
}

export function isBodyKinematicallyControlled({
  bodyUuid,
  getJointSnapshot,
  jointObjectsRef,
  obj,
  warningMessage,
}) {
  let isKinematicallyControlled = false;
  try {
    if (obj.userData?._pivotGroup || obj.parent?.userData?.isJointPivot) {
      isKinematicallyControlled = true;
    }
    if (!isKinematicallyControlled) {
      const jointIds = Object.keys(jointObjectsRef.current || {}).filter((jn) => {
        const entry = jointObjectsRef.current[jn];
        const bu = entry?.bodyGroup?.userData?.bodyUuid;
        return bu && bu === bodyUuid;
      });
      isKinematicallyControlled = jointIds.some((jn) => {
        const entry = jointObjectsRef.current[jn];
        if (entry?.bodyGroup?.userData?._pivotGroup) return true;
        if (entry?.bodyGroup?.parent?.userData?.isJointPivot) return true;
        const js = getJointSnapshot(jn);
        return js && Math.abs(Number(js.current) || 0) > 1e-6;
      });
    }
  } catch (error) {
    logger.warn(warningMessage, error);
    isKinematicallyControlled = false;
  }
  return isKinematicallyControlled;
}

export function syncBodyTransformsFromStore({
  getJointSnapshot,
  jointObjectsRef,
  objectsMap,
  sceneActions,
  warningMessage,
}) {
  const uniqueObjects = Array.from(new Set(Object.values(objectsMap)));
  uniqueObjects.forEach((obj) => {
    if (!obj) return;
    const bodyUuid = obj.userData?.bodyUuid;
    if (!bodyUuid) return;

    if (
      isBodyKinematicallyControlled({
        bodyUuid,
        getJointSnapshot,
        jointObjectsRef,
        obj,
        warningMessage,
      })
    ) {
      return;
    }

    const pos = sceneActions.getBodyPosition(bodyUuid);
    if (pos && obj.position) {
      obj.position.set(pos[0], pos[1], pos[2]);
    }
    const quat = sceneActions.getBodyRotation(bodyUuid);
    if (quat && obj.quaternion) {
      obj.quaternion.set(quat[1], quat[2], quat[3], quat[0]);
    }
  });
}
